import { useRef, useState } from 'react';
import { IListPresentation } from '../components/types';
import { useWidgetDndContextData } from 'src/context/WidgetDndContext';
import { useGetDeviceType } from 'src/hooks/useGetDeviceType';
import useGetItemsToIterate from '../hooks/useGetItemsToIterate';
import { useRefSetters } from './hooks/useRefSetters';
import { useGetLayoutBasedUtilities } from './utils/generalUtils';
import useResetLayoutScroll from './hooks/useResetLayoutScroll';
import getElementsToMap from './utils/getElementsToMap';

/** This file has all calculation & mutations of data globally to be available in the ListPresentation component
 * rendered on CFE */

export function useListPresentationData() {
  // Props
  // Used on cfe as this data is present even before rendering & is unchanging so no state setter is required
  const propsRef = useRef<IListPresentation>({});
  // Used on builder as this data is keeps changing & is not required as server side data
  const [propsState, setPropsState] = useState<IListPresentation>({});

  // States
  const {
    widgetContextState: { isBuilder, previewDevice, globalStyle, instaUserData },
  } = useWidgetDndContextData();
  const { deviceType } = useGetDeviceType();
  const isMobile = isBuilder ? previewDevice === 'mobile' : deviceType === 'mobile';

  // Variables
  const props = !isBuilder ? propsRef.current : propsState;
  const listPresentationConfig = props?.widgetOptions?.listPresentation || {};
  const cardFrameConfig = props?.widgetOptions?.cardFrame || {};

  const { iterableList, contentTypeConditions, moreItemsAvailable } =
    useGetItemsToIterate({
      cardFrameConfig,
      props,
      listPresentationConfig,
      instaUserData
    });
  const itemCountTotal = iterableList?.length;

  const {
    sectionContainerRefSetter,
    itemsContainerRefSetter,
    sectionContainerRef,
    itemContainerRef,
  } = useRefSetters();

  const { getConfigsAndUtils } = useGetLayoutBasedUtilities({
    listPresentationConfig,
    cardFrameConfig,
    isMobile,
    contentTypeConditions,
    props,
    globalStyle,
    sectionContainerRef,
    itemContainerRef,
    itemCountTotal,
    moreItemsAvailable,
    instaUserData
  });

  // Builder only
  useResetLayoutScroll({
    itemContainerRef,
    listPresentationConfig,
    columns: getConfigsAndUtils().generalValues.columns,
    isMobile,
  });

  const { elementsToMap } = getElementsToMap({
    contentTypeConditions,
    showAuthorImageList: getConfigsAndUtils().generalValues.showAuthorImageList,
    viewAllItemsConfig: getConfigsAndUtils().generalValues.viewAllItemsConfig,
    props,
  });

  function setProps(_props) {
    if (!isBuilder) {
      propsRef.current = _props;
    } else setPropsState(_props);
  }

  return {
    internalData: {
      utilities: { setProps },
    },
    externalData: {
      state: {
        listPresentationConfig,
        cardFrameConfig,
        isMobile,
        moreItemsAvailable,
        iterableList,
        itemCountTotal,
        contentTypeConditions,
      },
      elementsToMap,
      refs: { sectionContainerRef, itemContainerRef },
      refSetters: { sectionContainerRefSetter, itemsContainerRefSetter },
      utilities: {
        getConfigsAndUtils,
      },
      props,
    },
  };
}
